/*
 * Color filter swatch images
 * ---------------------------
 * When a collection's color filter is set to "Product image" mode, this fills
 * each color swatch with a representative variant photo pulled from the full
 * catalog. Liquid can only read the first ~50 products of a collection server
 * side, so for large collections most colors would otherwise fall back to a
 * plain circle. This resolves every colour client side from the collection's
 * products.json feed and caches the resulting color -> image map per session.
 *
 * Progressive enhancement: swatches render as color circles (or Search &
 * Discovery images) server side; this only swaps in an image where one is
 * missing and a match is found. Re-runs after AJAX filter re-renders.
 */
(function () {
  'use strict';

  var CACHE_PREFIX = 'raeColorSwatchImgs:';
  var PAGE_SIZE = 250;
  var MAX_PAGES = 20; // up to 5000 products (the "all" catalog on search); small
  // collections stop early when a page isn't full, so this cap only bites on search.

  function normalize(value) {
    return (value || '').trim().toLowerCase();
  }

  function sizedUrl(src, width) {
    if (!src) return src;
    try {
      var url = new URL(src, window.location.origin);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (error) {
      return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
    }
  }

  function buildMapInto(products, optionNames, map) {
    var names = optionNames.map(normalize);

    for (var i = 0; i < products.length; i++) {
      var product = products[i];
      var options = product.options || [];

      var position = 0;
      for (var o = 0; o < options.length; o++) {
        if (names.indexOf(normalize(options[o].name)) !== -1) {
          position = options[o].position;
          break;
        }
      }
      if (!position) continue;

      var productImg =
        (product.images && product.images[0] && product.images[0].src) ||
        (product.featured_image && product.featured_image.src) ||
        null;

      var variants = product.variants || [];
      for (var v = 0; v < variants.length; v++) {
        var variant = variants[v];
        var key = normalize(variant['option' + position]);
        if (!key || map[key]) continue;
        var img = (variant.featured_image && variant.featured_image.src) || productImg;
        if (img) map[key] = img;
      }
    }

    return map;
  }

  var pending = {};

  // Fetch the handle's products page by page, building the colour -> image map
  // incrementally. onPage(map) fires after each page so swatches can fill in
  // progressively. Paging stops when a page isn't full (small collections) or at
  // MAX_PAGES (the "all" catalog on search). Result is cached per handle.
  function getMap(handle, optionNames, onPage) {
    var cacheKey = CACHE_PREFIX + handle;

    try {
      var cached = sessionStorage.getItem(cacheKey);
      if (cached) return Promise.resolve(JSON.parse(cached));
    } catch (error) {
      /* sessionStorage unavailable */
    }

    if (pending[handle]) return pending[handle];

    var map = {};

    function fetchPage(page) {
      return fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + PAGE_SIZE + '&page=' + page)
        .then(function (response) {
          return response.ok ? response.json() : { products: [] };
        })
        .then(function (data) {
          var list = (data && data.products) || [];
          buildMapInto(list, optionNames, map);
          if (onPage) onPage(map);
          if (list.length === PAGE_SIZE && page < MAX_PAGES) return fetchPage(page + 1);
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(map));
          } catch (error) {
            /* quota / disabled — keep the in-memory result */
          }
          return map;
        });
    }

    pending[handle] = fetchPage(1).catch(function () {
      return map;
    });

    return pending[handle];
  }

  function applyMap(list, map) {
    var swatches = list.querySelectorAll('.facet-color-swatch');

    for (var i = 0; i < swatches.length; i++) {
      var swatch = swatches[i];
      if (swatch.querySelector('.facet-color-swatch__media')) continue;

      var labelEl = swatch.querySelector('.facet-color-swatch__label');
      var key = labelEl ? normalize(labelEl.textContent) : '';
      var src = map[key];
      if (!src) continue;

      var media = document.createElement('span');
      media.className = 'facet-color-swatch__media';

      var img = document.createElement('img');
      img.src = sizedUrl(src, 120);
      img.width = 60;
      img.height = 60;
      img.loading = 'lazy';
      img.alt = '';
      media.appendChild(img);

      var circle = swatch.querySelector('.facet-color-swatch__circle');
      if (circle) {
        circle.replaceWith(media);
      } else if (labelEl) {
        swatch.insertBefore(media, labelEl);
      } else {
        swatch.appendChild(media);
      }

      swatch.classList.add('facet-color-swatch--image');
    }
  }

  function process() {
    var lists = document.querySelectorAll('[data-color-swatch-images]');
    if (!lists.length) return;

    var groups = {};
    Array.prototype.forEach.call(lists, function (list) {
      var handle = list.getAttribute('data-collection-handle');
      if (!handle) return;
      if (!groups[handle]) {
        groups[handle] = {
          names: (list.getAttribute('data-color-option-names') || 'Color').split(','),
          lists: []
        };
      }
      groups[handle].lists.push(list);
    });

    Object.keys(groups).forEach(function (handle) {
      var group = groups[handle];
      var apply = function (map) {
        group.lists.forEach(function (list) {
          applyMap(list, map);
        });
      };
      getMap(handle, group.names, apply).then(apply);
    });
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () {
      scheduled = false;
      process();
    }, 150);
  }

  function init() {
    process();

    // Re-apply after AJAX re-renders. facets.js replaces the whole
    // #ProductGridContainer (which holds the sidebar filters) and swaps other
    // filters' innerHTML, so watch the document for any color-swatch list that
    // appears without images and re-process. Cheap: process() reads a cached map.
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var n = 0; n < added.length; n++) {
          var node = added[n];
          if (node.nodeType !== 1) continue;
          if (
            (node.matches && node.matches('[data-color-swatch-images]')) ||
            (node.querySelector && node.querySelector('[data-color-swatch-images]'))
          ) {
            schedule();
            return;
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
